import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  IconButton,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import {
  fetchCategories,
  fetchStoreItems,
  createCategory,
  updateCategory,
  deleteCategory,
  createStoreItem,
  updateStoreItem,
  deleteStoreItem,
  addItemVariant,
  Category,
  StoreItem,
  ItemVariant,
} from '../../store/slices/merchantSlice';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`store-tabpanel-${index}`}
      aria-labelledby={`store-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const StoreManagementPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { categories, storeItems, loadingCategories, loadingItems } = useSelector(
    (state: RootState) => state.merchant
  );

  const [tabValue, setTabValue] = useState(0);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openItemDialog, setOpenItemDialog] = useState(false);
  const [openVariantDialog, setOpenVariantDialog] = useState(false);

  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);

  const [isEditingItem, setIsEditingItem] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);

  const [selectedItemForVariant, setSelectedItemForVariant] = useState<string | null>(null);

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    sortOrder: 0,
  });

  const [itemForm, setItemForm] = useState({
    name: '',
    price: '',
    description: '',
    categoryId: '',
    imageUrl: '',
    inventoryTracking: false,
    stock: '',
  });

  const [variantForm, setVariantForm] = useState({
    type: '',
    value: '',
    priceOverride: '',
    stock: '',
    sku: '',
  });

  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(fetchStoreItems());
  }, [dispatch]);

  // Category handlers
  const handleOpenCategoryDialog = (category?: Category) => {
    if (category) {
      setIsEditingCategory(true);
      setEditingCategoryId(category.id);
      setCategoryForm({
        name: category.name,
        description: category.description || '',
        sortOrder: category.sortOrder,
      });
    } else {
      setIsEditingCategory(false);
      setEditingCategoryId(null);
      setCategoryForm({ name: '', description: '', sortOrder: 0 });
    }
    setOpenCategoryDialog(true);
  };

  const handleSaveCategory = async () => {
    if (isEditingCategory && editingCategoryId) {
      await dispatch(updateCategory({ id: editingCategoryId, data: categoryForm }));
    } else {
      await dispatch(createCategory(categoryForm));
    }
    setOpenCategoryDialog(false);
  };

  const handleDeleteCategory = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      await dispatch(deleteCategory(id));
    }
  };

  // Item handlers
  const handleOpenItemDialog = (item?: StoreItem) => {
    if (item) {
      setIsEditingItem(true);
      setEditingItemId(item.id);
      setItemForm({
        name: item.name,
        price: item.price.toString(),
        description: item.description || '',
        categoryId: item.categoryId,
        imageUrl: item.imageUrl || '',
        inventoryTracking: item.inventoryTracking,
        stock: item.stock.toString(),
      });
    } else {
      setIsEditingItem(false);
      setEditingItemId(null);
      setItemForm({
        name: '',
        price: '',
        description: '',
        categoryId: '',
        imageUrl: '',
        inventoryTracking: false,
        stock: '',
      });
    }
    setOpenItemDialog(true);
  };

  const handleSaveItem = async () => {
    const submitData = {
      name: itemForm.name,
      price: parseFloat(itemForm.price),
      description: itemForm.description,
      categoryId: itemForm.categoryId,
      imageUrl: itemForm.imageUrl,
      inventoryTracking: itemForm.inventoryTracking,
      stock: parseInt(itemForm.stock),
    };

    if (isEditingItem && editingItemId) {
      await dispatch(updateStoreItem({ id: editingItemId, data: submitData }));
    } else {
      await dispatch(createStoreItem(submitData));
    }
    setOpenItemDialog(false);
  };

  const handleDeleteItem = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      await dispatch(deleteStoreItem(id));
    }
  };

  // Variant handlers
  const handleOpenVariantDialog = (itemId: string) => {
    setSelectedItemForVariant(itemId);
    setVariantForm({ type: '', value: '', priceOverride: '', stock: '', sku: '' });
    setOpenVariantDialog(true);
  };

  const handleSaveVariant = async () => {
    if (!selectedItemForVariant) return;

    const submitData = {
      type: variantForm.type,
      value: variantForm.value,
      priceOverride: variantForm.priceOverride ? parseFloat(variantForm.priceOverride) : undefined,
      stock: parseInt(variantForm.stock),
      sku: variantForm.sku,
    };

    await dispatch(addItemVariant({ itemId: selectedItemForVariant, data: submitData }));
    setOpenVariantDialog(false);
  };

  const handleInputChange = (setter: any) => (e: any) => {
    const { name, value, type, checked } = e.target;
    setter((prev: any) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  return (
    <Box sx={{ pb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{
          fontWeight: 700,
          color: '#202647',
          fontSize: { xs: '1.5rem', md: '2rem' },
        }}>
          Store Management
        </Typography>
        <Typography variant="body2" sx={{ color: '#7F839C', mt: 0.5 }}>
          Manage your products, categories, and variants
        </Typography>
      </Box>

      {/* Tabs */}
      <Card sx={{ borderRadius: 3 }}>
        <Box sx={{ borderBottom: '1px solid #E8E8F0' }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                color: '#7F839C',
              },
              '& .Mui-selected': {
                color: '#11C1EC',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#11C1EC',
              },
            }}
          >
            <Tab label="Categories" id="store-tab-0" />
            <Tab label="Items" id="store-tab-1" />
          </Tabs>
        </Box>

        {/* Categories Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenCategoryDialog()}
            >
              Add Category
            </Button>
          </Box>

          {loadingCategories ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : categories.length === 0 ? (
            <Card sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" sx={{ color: '#7F839C' }}>
                No categories yet. Create one to organize your products.
              </Typography>
            </Card>
          ) : (
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#F8F8FC' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Sort Order</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categories.map((category) => (
                      <TableRow key={category.id}>
                        <TableCell sx={{ color: '#565972', fontWeight: 500 }}>
                          {category.name}
                        </TableCell>
                        <TableCell sx={{ color: '#7F839C', fontSize: '0.9rem' }}>
                          {category.description || '-'}
                        </TableCell>
                        <TableCell sx={{ color: '#565972' }}>{category.sortOrder}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenCategoryDialog(category)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteCategory(category.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Mobile Category Cards */}
          <Grid container spacing={2} sx={{ display: { xs: 'flex', md: 'none' } }}>
            {categories.map((category) => (
              <Grid size={{ xs: 12 }} key={category.id}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#202647', mb: 1 }}>
                      {category.name}
                    </Typography>
                    {category.description && (
                      <Typography variant="body2" sx={{ color: '#7F839C', mb: 1.5 }}>
                        {category.description}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: '#7F839C', display: 'block', mb: 1.5 }}>
                      Sort Order: {category.sortOrder}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        startIcon={<EditIcon fontSize="small" />}
                        onClick={() => handleOpenCategoryDialog(category)}
                      >
                        Edit
                      </Button>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteCategory(category.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* Items Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenItemDialog()}
            >
              Add Item
            </Button>
          </Box>

          {loadingItems ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : storeItems.length === 0 ? (
            <Card sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" sx={{ color: '#7F839C' }}>
                No items yet. Create one to start selling.
              </Typography>
            </Card>
          ) : (
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#F8F8FC' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Price</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Stock</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Variants</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {storeItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell sx={{ color: '#565972', fontWeight: 500 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {item.imageUrl ? (
                              <img src={item.imageUrl} alt={item.name}
                                style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover' }} />
                            ) : (
                              <Box sx={{ width: 40, height: 40, borderRadius: '6px', bgcolor: '#F0F0F6',
                                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <ImageIcon sx={{ color: '#B0B3C7', fontSize: 20 }} />
                              </Box>
                            )}
                            {item.name}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: '#565972' }}>${item.price.toFixed(2)}</TableCell>
                        <TableCell sx={{ color: '#565972' }}>{item.stock}</TableCell>
                        <TableCell sx={{ color: '#565972' }}>{item.variants.length}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenVariantDialog(item.id)}
                            title="Add Variant"
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenItemDialog(item)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteItem(item.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Mobile Item Cards */}
          <Grid container spacing={2} sx={{ display: { xs: 'flex', md: 'none' } }}>
            {storeItems.map((item) => (
              <Grid size={{ xs: 12 }} key={item.id}>
                <Card sx={{ borderRadius: 3 }}>
                  {item.imageUrl && (
                    <Box sx={{ width: '100%', height: 160, overflow: 'hidden' }}>
                      <img src={item.imageUrl} alt={item.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                  )}
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#202647', mb: 1 }}>
                      {item.name}
                    </Typography>
                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" sx={{ color: '#7F839C' }}>Price</Typography>
                        <Typography sx={{ fontWeight: 600, color: '#202647' }}>
                          ${item.price.toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" sx={{ color: '#7F839C' }}>Stock</Typography>
                        <Typography sx={{ fontWeight: 600, color: '#202647' }}>
                          {item.stock}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Typography variant="caption" sx={{ color: '#7F839C', display: 'block', mb: 1.5 }}>
                      Variants: {item.variants.length}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        startIcon={<AddIcon fontSize="small" />}
                        onClick={() => handleOpenVariantDialog(item.id)}
                      >
                        Add Variant
                      </Button>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenItemDialog(item)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteItem(item.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>
      </Card>

      {/* Category Dialog */}
      <Dialog
        open={openCategoryDialog}
        onClose={() => setOpenCategoryDialog(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#202647', bgcolor: '#F8F8FC' }}>
          {isEditingCategory ? 'Edit Category' : 'Add Category'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Category Name"
              name="name"
              value={categoryForm.name}
              onChange={handleInputChange(setCategoryForm)}
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={categoryForm.description}
              onChange={handleInputChange(setCategoryForm)}
              multiline
              rows={2}
            />
            <TextField
              fullWidth
              label="Sort Order"
              name="sortOrder"
              type="number"
              value={categoryForm.sortOrder}
              onChange={handleInputChange(setCategoryForm)}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #E8E8F0' }}>
          <Button onClick={() => setOpenCategoryDialog(false)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleSaveCategory} variant="contained">
            {isEditingCategory ? 'Update' : 'Create'} Category
          </Button>
        </DialogActions>
      </Dialog>

      {/* Item Dialog */}
      <Dialog
        open={openItemDialog}
        onClose={() => setOpenItemDialog(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#202647', bgcolor: '#F8F8FC' }}>
          {isEditingItem ? 'Edit Item' : 'Add Item'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Item Name"
              name="name"
              value={itemForm.name}
              onChange={handleInputChange(setItemForm)}
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Price"
                  name="price"
                  type="number"
                  value={itemForm.price}
                  onChange={handleInputChange(setItemForm)}
                  inputProps={{ step: '0.01' }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="categoryId"
                    value={itemForm.categoryId}
                    onChange={handleInputChange(setItemForm)}
                    label="Category"
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={itemForm.description}
              onChange={handleInputChange(setItemForm)}
              multiline
              rows={2}
            />
            <Box>
              <Typography variant="body2" sx={{ mb: 1, color: '#565972' }}>Product Image</Typography>
              {itemForm.imageUrl && (
                <Box sx={{ mb: 1.5, textAlign: 'center' }}>
                  <img
                    src={itemForm.imageUrl}
                    alt="Product preview"
                    style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 8, objectFit: 'cover' }}
                  />
                </Box>
              )}
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                fullWidth
                sx={{ borderColor: '#11C1EC', color: '#11C1EC' }}
              >
                {itemForm.imageUrl ? 'Change Image' : 'Upload Image'}
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 5 * 1024 * 1024) {
                        alert('Image must be less than 5MB');
                        return;
                      }
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setItemForm(prev => ({ ...prev, imageUrl: reader.result as string }));
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
              </Button>
            </Box>
            <TextField
              fullWidth
              label="Stock"
              name="stock"
              type="number"
              value={itemForm.stock}
              onChange={handleInputChange(setItemForm)}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #E8E8F0' }}>
          <Button onClick={() => setOpenItemDialog(false)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleSaveItem} variant="contained">
            {isEditingItem ? 'Update' : 'Create'} Item
          </Button>
        </DialogActions>
      </Dialog>

      {/* Variant Dialog */}
      <Dialog
        open={openVariantDialog}
        onClose={() => setOpenVariantDialog(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#202647', bgcolor: '#F8F8FC' }}>
          Add Variant
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Variant Type"
              name="type"
              value={variantForm.type}
              onChange={handleInputChange(setVariantForm)}
              placeholder="e.g., Color, Size"
            />
            <TextField
              fullWidth
              label="Variant Value"
              name="value"
              value={variantForm.value}
              onChange={handleInputChange(setVariantForm)}
              placeholder="e.g., Red, Large"
            />
            <TextField
              fullWidth
              label="Price Override (Optional)"
              name="priceOverride"
              type="number"
              value={variantForm.priceOverride}
              onChange={handleInputChange(setVariantForm)}
              inputProps={{ step: '0.01' }}
            />
            <TextField
              fullWidth
              label="Stock"
              name="stock"
              type="number"
              value={variantForm.stock}
              onChange={handleInputChange(setVariantForm)}
            />
            <TextField
              fullWidth
              label="SKU"
              name="sku"
              value={variantForm.sku}
              onChange={handleInputChange(setVariantForm)}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #E8E8F0' }}>
          <Button onClick={() => setOpenVariantDialog(false)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleSaveVariant} variant="contained">
            Add Variant
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StoreManagementPage;
